import type { SolutionPageContent } from '../../types/solution'

export const servicesSolution: SolutionPageContent = {
  slug: 'services',
  hero: {
    headline: 'Spend your time on clients, not paperwork',
    subheadline: 'Keep client records, charge for every job, and see who still owes you — all from your phone.',
    card: {
      type: 'list',
      title: 'Today · Kimironko studio',
      badge: { label: '8 clients', tone: 'brand' },
      rows: [
        { label: 'Claudine M.', sublabel: 'Braids · 10:00', value: 'RWF 25,000', tone: 'positive' },
        { label: 'Yvette K.', sublabel: 'Manicure · 12:30', value: 'RWF 8,000' },
        { label: 'Sandrine U.', sublabel: 'Relaxer · 15:00', value: 'RWF 15,000' },
      ],
      footer: { label: 'Collected today', value: 'RWF 112,000' },
    },
  },
  challenges: [
    {
      quote: 'I forget to charge for small extras on a job.',
      detail: 'A treatment here, a product there — it adds up at the end of the month.',
    },
    {
      quote: 'Clients say they’ll pay later, and later never comes.',
      detail: 'I don’t want to chase regulars, but I can’t keep losing money.',
    },
    {
      quote: 'I don’t know which services earn the most.',
      detail: 'I’m busy all day, but I’m not sure what actually pays the rent.',
    },
  ],
  helps: [
    {
      solves: 'I forget to charge for small extras',
      title: 'Charge for every service and product',
      copy: 'Add services and products to one bill, take payment any way the client likes, and send a digital receipt.',
      product: 'sales-pos',
      visual: {
        kind: 'card',
        card: {
          type: 'list',
          title: 'Bill · Claudine M.',
          rows: [
            { label: 'Braids (medium)', value: 'RWF 20,000' },
            { label: 'Hair treatment', value: 'RWF 3,000' },
            { label: 'Edge control gel', value: 'RWF 2,000' },
          ],
          footer: { label: 'Total', value: 'RWF 25,000' },
          action: 'Take payment',
        },
      },
    },
    {
      solves: 'Clients say they’ll pay later',
      title: 'Client history and balances',
      copy: 'See each client’s visits, preferences, and what they owe. Send a friendly SMS reminder in one tap.',
      product: 'customers',
      visual: {
        kind: 'card',
        card: {
          type: 'list',
          title: 'Client balances',
          rows: [
            { label: 'Yvette K.', sublabel: 'Last visit 12 Sep', value: 'RWF 16,000', tone: 'warning' },
            { label: 'Grace I.', sublabel: 'Last visit 18 Sep', value: 'RWF 8,000' },
            { label: 'Diane U.', sublabel: 'Last visit 20 Sep', value: 'Settled', tone: 'positive' },
          ],
          action: 'Send reminders',
        },
      },
    },
    {
      solves: 'I don’t know which services earn the most',
      title: 'Revenue by service and staff',
      copy: 'See which services bring in the most and how each team member is performing, week by week.',
      product: 'reports',
      visual: {
        kind: 'card',
        card: {
          type: 'progress',
          title: 'Revenue by service · September',
          rows: [
            { label: 'Braids', value: 'RWF 1.2M', pct: 100, tone: 'positive' },
            { label: 'Relaxer & styling', value: 'RWF 640K', pct: 53 },
            { label: 'Manicure & pedicure', value: 'RWF 410K', pct: 34 },
          ],
        },
      },
    },
  ],
  journey: {
    heading: 'A day with Kayko',
    subheading: 'How a busy service day runs.',
    items: [
      { time: '8:00', title: 'See today’s clients', copy: 'Check who’s coming and what they usually book.' },
      { time: '10:00', title: 'Complete a job', copy: 'Add the service and any products to the bill.' },
      { time: '10:45', title: 'Get paid', copy: 'Mobile money, card, or cash, with a digital receipt.' },
      { time: '19:00', title: 'Review the day', copy: 'See takings, top services, and open balances.' },
    ],
  },
  results: [
    { value: '+15%', label: 'more revenue from extras charged correctly' },
    { value: '−50%', label: 'less money owed by clients' },
    { value: '5 min', label: 'to close the day and check takings' },
  ],
  testimonial: {
    quote: 'Every product and treatment is on the bill now, and my regulars actually settle their balances.',
    name: 'Grace Mutoni',
    role: 'Owner',
    business: 'Mutoni Beauty Studio',
    location: 'Kimironko, Kigali',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/5711f857-bdbd-403b-9f97-8abca4b7ec9b.jpg',
    metric: { value: '−50%', label: 'less money owed by clients' },
  },
  products: ['sales-pos', 'customers', 'expenses', 'reports'],
  plan: { name: 'Starter', audience: 'service businesses' },
  faq: [
    {
      question: 'Can I sell services and products on the same bill?',
      answer: 'Yes. Add services, products, or both to a single bill and take one payment.',
    },
    {
      question: 'Can I keep notes on each client?',
      answer: 'Yes. Save preferences, past services, and notes so any team member can pick up where you left off.',
    },
    {
      question: 'Can I see what each staff member earns?',
      answer: 'Yes. Assign each service to a team member and see revenue by staff in your reports.',
    },
    {
      question: 'Can I take deposits for larger jobs?',
      answer: 'Yes. Record a deposit, and Kayko tracks the balance until the job is paid in full.',
    },
    {
      question: 'Do I need a computer?',
      answer: 'No. Kayko works fully on a phone or tablet.',
    },
  ],
  related: ['sell-and-get-paid', 'healthcare', 'understand-performance'],
  cta: {
    headline: 'Get paid for every job you do',
    subheadline: 'Set up your services in minutes. Local support in Kigali when you need it.',
  },
}
